import { Children, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Printer, ArrowLeft, Loader2, Menu } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import HelpChat from "@/components/HelpChat";

const ANCHOR_RE = /\s*\{#([a-z0-9][a-z0-9_-]*)\}\s*$/;

// Kop-renderer: haalt het `{#anker}` uit de koptekst en zet het als id op het element.
const heading = (Tag, cls) => function Heading({ node, children, ...props }) {
  const arr = Children.toArray(children);
  let id;
  const last = arr[arr.length - 1];
  if (typeof last === "string") {
    const m = last.match(ANCHOR_RE);
    if (m) { id = m[1]; arr[arr.length - 1] = last.replace(ANCHOR_RE, ""); }
  }
  return <Tag id={id} className={`${cls} scroll-mt-24 group`} {...props}>{arr}{id && (
    <a href={`#${id}`} className="ml-2 text-muted-foreground/40 opacity-0 group-hover:opacity-100 no-underline text-base font-normal" aria-label="Link naar deze sectie">#</a>
  )}</Tag>;
};

const components = {
  h1: heading("h1", "font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-2 mb-6 leading-none"),
  h2: heading("h2", "font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-14 mb-4 pb-2 border-b-2 border-foreground"),
  h3: heading("h3", "font-display font-bold text-xl uppercase tracking-tight mt-10 mb-3"),
  h4: heading("h4", "font-display font-bold text-base uppercase tracking-wide mt-6 mb-2"),
  p: ({ node, ...p }) => <p className="my-3 leading-relaxed text-[15px]" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-3 pl-6 list-disc space-y-1.5 text-[15px]" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-3 pl-6 list-decimal space-y-1.5 text-[15px]" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="my-5 border-l-4 border-primary bg-primary/5 px-4 py-3 text-sm [&_p]:my-1" {...p} />,
  hr: () => <hr className="my-10 border-t-2 border-foreground/20" />,
  strong: ({ node, ...p }) => <strong className="font-bold" {...p} />,
  code: ({ node, className, children, ...p }) => (
    String(className || "").includes("language-") || String(children).includes("\n")
      ? <code className="font-mono-num text-xs" {...p}>{children}</code>
      : <code className="font-mono-num text-[13px] bg-muted px-1 py-0.5 border border-border" {...p}>{children}</code>
  ),
  pre: ({ node, ...p }) => <pre className="my-4 bg-foreground text-background p-4 overflow-x-auto text-xs leading-relaxed" {...p} />,
  table: ({ node, ...p }) => <div className="my-5 overflow-x-auto border-2 border-foreground"><table className="w-full text-sm [&_td:first-child]:min-w-[8.5rem]" {...p} /></div>,
  thead: ({ node, ...p }) => <thead className="bg-foreground text-background" {...p} />,
  th: ({ node, ...p }) => <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest" {...p} />,
  td: ({ node, ...p }) => <td className="px-3 py-2 border-t border-border align-top [overflow-wrap:anywhere]" {...p} />,
  tr: ({ node, ...p }) => <tr className="even:bg-muted/40" {...p} />,
  a: ({ node, href = "", ...p }) => href.startsWith("#")
    ? <a href={href} className="text-primary underline decoration-dotted underline-offset-4 hover:decoration-solid" {...p} />
    : <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4" {...p} />,
  img: ({ node, src = "", alt = "", ...p }) => {
    const url = src.startsWith("images/") ? `${API_BASE}/manual/images/${src.slice(7)}` : src;
    return (
      <figure className="my-6" data-testid="manual-figure">
        <img src={url} alt={alt} loading="lazy" className="w-full border-2 border-foreground heavy-shadow bg-card" {...p} />
        {alt && <figcaption className="stat-label mt-3 normal-case tracking-wide text-[11px]">{alt}</figcaption>}
      </figure>
    );
  },
};

function Toc({ anchors, active, onNavigate }) {
  return (
    <nav aria-label="Inhoudsopgave" data-testid="manual-toc" className="text-sm">
      <div className="stat-label mb-3">Inhoud</div>
      <ul className="space-y-0.5">
        {anchors.filter((a) => a.level <= 3).map((a) => (
          <li key={a.id}>
            <a
              href={`#${a.id}`}
              onClick={() => onNavigate?.()}
              data-testid={`toc-${a.id}`}
              className={`block py-1 border-l-2 leading-snug transition-colors ${a.level === 1 ? "pl-3 font-display uppercase font-black tracking-tight mt-3" : a.level === 2 ? "pl-3 font-bold" : "pl-6 text-muted-foreground"} ${active === a.id ? "border-primary text-primary" : "border-transparent hover:border-foreground/40 hover:text-foreground"}`}
            >
              {a.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function ManualPage() {
  const { hash } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const active = hash.replace(/^#/, "");

  useEffect(() => {
    api.get("/manual").then((r) => setData(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, []);

  const scrollToHash = useCallback(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    if (!data) return;
    // Spring naar het anker; herhaal zodra afbeeldingen laden (layout verschuift) tot de gebruiker zelf scrolt.
    let pending = Boolean(window.location.hash);
    const stop = () => { pending = false; };
    const rescroll = () => { if (pending) scrollToHash(); };
    const t = setTimeout(scrollToHash, 60);
    const imgs = [...document.querySelectorAll('[data-testid="manual-content"] img')].filter((i) => !i.complete);
    imgs.forEach((i) => i.addEventListener("load", rescroll));
    ["wheel", "touchstart", "keydown"].forEach((ev) => window.addEventListener(ev, stop, { passive: true }));
    const end = setTimeout(stop, 6000);
    const onHash = () => { pending = true; scrollToHash(); };
    window.addEventListener("hashchange", onHash);
    return () => {
      clearTimeout(t); clearTimeout(end);
      imgs.forEach((i) => i.removeEventListener("load", rescroll));
      ["wheel", "touchstart", "keydown"].forEach((ev) => window.removeEventListener(ev, stop));
      window.removeEventListener("hashchange", onHash);
    };
  }, [data, scrollToHash]);

  const anchors = useMemo(() => data?.anchors || [], [data]);
  const updated = data?.updated_at ? new Date(data.updated_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="manual-page">
      <header className="border-b-2 border-foreground/90 bg-background sticky top-0 z-40 print:hidden">
        <div className="max-w-[1400px] mx-auto flex items-center gap-4 px-4 sm:px-6 py-3">
          <button onClick={() => setTocOpen((v) => !v)} className="lg:hidden btn-brutal bg-background w-10 h-10 inline-flex items-center justify-center" aria-label="Inhoudsopgave" data-testid="manual-toc-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center h-10"><img src="/logo-meerjarenplan.png" alt="MeerjarenplanOnline.nl" className="h-9 w-auto" /></Link>
          <div className="hidden sm:flex items-center gap-2 font-display uppercase font-black tracking-wide text-sm">
            <BookOpen className="w-4 h-4" /> Handleiding
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="manual-print-btn"
              className="btn-brutal bg-background text-foreground h-10 px-3 inline-flex items-center gap-2 font-display uppercase font-bold text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Printen</span>
            </button>
            <Link to="/" data-testid="manual-back-app"
              className="btn-brutal bg-primary text-primary-foreground h-10 px-3 inline-flex items-center gap-2 font-display uppercase font-bold text-sm">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Naar de app</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
        <aside className={`${tocOpen ? "block" : "hidden"} lg:block mb-8 lg:mb-0 print:hidden`}>
          <div className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto pr-2 border-2 border-foreground bg-card p-4 heavy-shadow lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
            {data ? <Toc anchors={anchors} active={active} onNavigate={() => setTocOpen(false)} /> : <div className="stat-label">Laden…</div>}
            {data && (
              <div className="mt-6 pt-4 border-t border-border text-[11px] text-muted-foreground" data-testid="manual-meta">
                {data.source === "custom" ? <>Aangepaste versie{updated ? ` · ${updated}` : ""}</> : "Standaardversie"}
              </div>
            )}
          </div>
        </aside>

        <main className="min-w-0 max-w-[820px]" data-testid="manual-content">
          {error && <div className="border-2 border-destructive bg-destructive/10 text-destructive p-4" data-testid="manual-error">{error}</div>}
          {!data && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Handleiding laden…</div>}
          {data && <Markdown remarkPlugins={[remarkGfm]} components={components}>{data.markdown}</Markdown>}
        </main>
      </div>
      <HelpChat />
    </div>
  );
}
